/**
 * TEMPORARY — /playground
 *
 * Data downloads for the travel-time / traffic ETA project, plus Whisper QA
 * for support-uploaded call recordings. Streams CSV dumps of the
 * rider-movement tables from the BI backend's read-only `/geo/playground`
 * endpoints. Remove this page (and backend/app/routers/playground.py) once
 * those tools are no longer needed.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Database, Download, HardDriveDownload, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { geoGet } from '@/api/geo/client';
import { apiClient, getAccessToken } from '@/api/client';
import { env } from '@/lib/env';
import { CallTranscriptionSection } from './CallTranscriptionSection';

interface PlaygroundDataset {
  slug: string;
  description: string;
  approx_rows: number;
  columns: string[];
  first_ts: string | null;
  last_ts: string | null;
  date_filterable: boolean;
}

const nf = new Intl.NumberFormat('en-US');

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - 29 * 24 * 3600 * 1000);
  return { from: isoDate(from), to: isoDate(to) };
}

/** Exclusive end bound: the day after `to`, as YYYY-MM-DD. */
function endExclusive(to: string): string {
  const d = new Date(`${to}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return isoDate(d);
}

function geoBase(): string {
  let base = env.geoApiBase || '/geo';
  if (base.startsWith('/') && typeof window !== 'undefined') {
    base = `${window.location.origin}${base}`;
  }
  return base;
}

export function PlaygroundPage() {
  const [{ from, to }, setRange] = useState(defaultRange);
  const [downloading, setDownloading] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['playground-datasets'],
    queryFn: () => geoGet<{ datasets: PlaygroundDataset[] }>('/playground/datasets'),
    staleTime: 5 * 60 * 1000,
  });

  const rangeValid = useMemo(() => Boolean(from && to && from <= to), [from, to]);

  async function download(ds: PlaygroundDataset, gz: boolean) {
    const key = `${ds.slug}:${gz}`;
    setDownloading(key);
    try {
      // Any authed Django call runs the main client's refresh flow, so the
      // token we embed in the download URL is fresh (streams can outlive it;
      // it is only checked once at request start).
      await apiClient.get('/feature/bi/api/access/').catch(() => undefined);
      const token = getAccessToken();
      if (!token) throw new Error('no token');

      const params = new URLSearchParams();
      if (ds.date_filterable) {
        params.set('start', from);
        params.set('end', endExclusive(to));
      }
      if (gz) params.set('gz', '1');
      params.set('token', token);

      const a = document.createElement('a');
      a.href = `${geoBase()}/playground/export/${ds.slug}?${params.toString()}`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      // The browser handles the stream; we only debounce double-clicks.
      window.setTimeout(() => setDownloading((k) => (k === key ? null : k)), 2500);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Temporary"
        title="Data Playground"
        description={
          'CSV dumps of rider-movement data for the travel-time (traffic ETA) project, ' +
          'plus Whisper transcription QA for support call recordings. Read-only against ' +
          'the production database; large ping tables are streamed — prefer gzip and ' +
          'export month-by-month.'
        }
        actions={
          <div className="flex items-end gap-2">
            <label className="flex flex-col gap-1 text-[11px] font-medium text-muted-foreground">
              From
              <Input
                type="date"
                value={from}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                className="h-8 w-[150px] text-xs"
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] font-medium text-muted-foreground">
              To (inclusive)
              <Input
                type="date"
                value={to}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                className="h-8 w-[150px] text-xs"
              />
            </label>
          </div>
        }
      />

      {isError ? (
        <ErrorState title="Failed to load datasets" onRetry={() => void refetch()} />
      ) : isLoading || !data ? (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-44 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {data.datasets.map((ds) => {
            const busy = downloading === `${ds.slug}:true` || downloading === `${ds.slug}:false`;
            return (
              <Card key={ds.slug}>
                <CardHeader>
                  <div className="min-w-0">
                    <CardTitle className="flex items-center gap-2 font-mono text-[13px] text-foreground">
                      <Database className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      {ds.slug}
                    </CardTitle>
                    <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{ds.description}</p>
                  </div>
                </CardHeader>
                <CardBody className="flex flex-col gap-3">
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-muted-foreground">
                    <span>
                      ~<span className="font-medium text-foreground">{nf.format(ds.approx_rows)}</span> rows
                    </span>
                    {ds.first_ts && ds.last_ts ? (
                      <span>
                        coverage{' '}
                        <span className="font-medium text-foreground">
                          {ds.first_ts.slice(0, 10)} → {ds.last_ts.slice(0, 10)}
                        </span>
                      </span>
                    ) : (
                      <span>reference table (full dump, date range ignored)</span>
                    )}
                  </div>
                  <p className="break-words font-mono text-[10px] leading-relaxed text-muted-foreground/80">
                    {ds.columns.join(', ')}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy || (ds.date_filterable && !rangeValid)}
                      onClick={() => void download(ds, true)}
                    >
                      {busy ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                      ) : (
                        <HardDriveDownload className="h-3.5 w-3.5" aria-hidden />
                      )}
                      CSV (gzip)
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy || (ds.date_filterable && !rangeValid)}
                      onClick={() => void download(ds, false)}
                    >
                      <Download className="h-3.5 w-3.5" aria-hidden />
                      Plain CSV
                    </Button>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      <CallTranscriptionSection
        from={from}
        to={to}
        rangeValid={rangeValid}
        endExclusive={endExclusive}
      />
    </>
  );
}
